import { useRef, useEffect, useLayoutEffect, useCallback, useState } from 'react';
import { createRoot, Root } from 'react-dom/client';
import {
  EditorView,
  keymap,
  lineNumbers,
  Decoration,
  DecorationSet,
  ViewPlugin,
  ViewUpdate,
  WidgetType
} from '@codemirror/view';
import { EditorState, Compartment, Prec, Range, StateEffect } from '@codemirror/state';
import { markdown } from '@codemirror/lang-markdown';
import { autocompletion, CompletionContext } from '@codemirror/autocomplete';
import { defaultKeymap, indentWithTab } from '@codemirror/commands';
import { searchKeymap } from '@codemirror/search';
import { ReportElementUnion } from '../../types/report.types';
import { DataSet } from '../../types/dataset.types';
import { AspectRatioPreset } from '../../types/graph-builder.types';
import { ReportElementRenderer } from './ReportElementRenderer';
import { ReportElementMenu } from './ReportElementMenu';
import { REPORT_ELEMENT_SIZING } from '../constants/reportElementSizing';
import { getAspectRatioCssValue } from '../utils/aspectRatioUtils';
import * as styles from './ReportMarkdownEditor.css';

// インラインプレビューコンポーネント（折りたたみ可能）
interface ElementPreviewInlineProps {
  elementType: string;
  elementId: string;
  element: ReportElementUnion | null;
  datasets: Record<string, DataSet>;
  width: number | 'fit';
  height: number;
  aspectRatio: AspectRatioPreset;
  // メニュー用コールバック
  onDeleteElement?: (elementId: string) => void;
  onEditElement?: (elementId: string) => void;
  onDuplicateElement?: (elementId: string) => void;
  onOpenSourceDataset?: (elementId: string) => void;
  onExportElement?: (elementId: string) => void;
  onResizeElement?: (elementId: string) => void;
  /** レポート内での参照回数 */
  referenceCount?: number;
}

function ElementPreviewInline({
  elementType,
  elementId,
  element,
  datasets,
  width,
  height,
  aspectRatio,
  onDeleteElement,
  onEditElement,
  onDuplicateElement,
  onOpenSourceDataset,
  onExportElement,
  onResizeElement,
  referenceCount
}: ElementPreviewInlineProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const title = element && 'title' in element && element.title ? element.title : null;
  const aspectRatioCss = getAspectRatioCssValue(aspectRatio);

  if (!element) {
    return (
      <span className={styles.inlinePreviewMissing}>
        [Missing: {elementType}]
      </span>
    );
  }

  return (
    <div className={styles.inlinePreviewContainer}>
      <div
        className={styles.inlinePreviewHeader}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsExpanded(!isExpanded);
        }}
      >
        <span className={styles.inlinePreviewExpandIcon}>
          {isExpanded ? '▼' : '▶'}
        </span>
        <span className={styles.inlinePreviewBadge}>
          {title ? `${elementType}: ${title}` : elementType}
        </span>
      </div>
      {isExpanded && (
        <div
          className={styles.inlinePreviewContent}
          style={{
            width: width === 'fit' ? '100%' : `${width}px`,
            aspectRatio: aspectRatioCss,
            height: aspectRatio === 'custom' ? `${height}px` : undefined,
          }}
        >
          <div className={styles.elementMenuWrapper}>
            <ReportElementMenu
              elementId={elementId}
              elementType={elementType}
              onDeleteElement={onDeleteElement || (() => {})}
              onEditElement={onEditElement}
              onDuplicateElement={onDuplicateElement}
              onOpenSourceDataset={onOpenSourceDataset}
              onExportElement={onExportElement}
              onResizeElement={onResizeElement}
              hasDatasetId={'datasetId' in element && !!element.datasetId}
              referenceCount={referenceCount}
              context="inline"
            />
          </div>
          <ReportElementRenderer
            element={element}
            datasets={datasets}
            width={width}
            // aspectRatio指定時はheightを渡さない（親コンテナに合わせる）
            // 'custom'の場合のみ明示的な高さを指定
            height={aspectRatio === 'custom' ? height : undefined}
          />
        </div>
      )}
    </div>
  );
}

interface ReportMarkdownEditorProps {
  content: string;
  elements: Record<string, ReportElementUnion>;
  datasets: Record<string, DataSet>;
  onChange: (content: string) => void;
  onClose: () => void;
  // 要素操作用コールバック
  onDeleteElement?: (elementId: string) => void;
  onEditElement?: (elementId: string) => void;
  onDuplicateElement?: (elementId: string) => void;
  onOpenSourceDataset?: (elementId: string) => void;
  onExportElement?: (elementId: string) => void;
  onResizeElement?: (elementId: string) => void;
}

/**
 * ReportMarkdownEditor - CodeMirrorベースのMarkdownエディタ
 *
 * 機能:
 * - Markdownシンタックスハイライト
 * - {{type:id}} 形式の要素参照のオートコンプリート
 * - 編集内容は随時onChange経由で保存（300msデバウンス）
 * - Ctrl+S / Escape でエディタを閉じる
 */
export function ReportMarkdownEditor({
  content,
  elements,
  datasets,
  onChange,
  onClose,
  onDeleteElement,
  onEditElement,
  onDuplicateElement,
  onOpenSourceDataset,
  onExportElement,
  onResizeElement
}: ReportMarkdownEditorProps) {
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const editorViewRef = useRef<EditorView | null>(null);
  const editableCompartment = useRef(new Compartment());

  // 要素データを常に最新の状態で参照するためのref
  const elementsRef = useRef(elements);
  const datasetsRef = useRef(datasets);

  // 現在のコンテンツを保持するref（参照回数カウント用）
  const contentRef = useRef(content);

  // 要素の参照回数をカウントするヘルパー関数
  const countElementReferences = useCallback((elementId: string): number => {
    const currentContent = editorViewRef.current?.state.doc.toString() || contentRef.current;
    const pattern = new RegExp(`\\{\\{\\w+:${elementId}\\}\\}`, 'g');
    const matches = currentContent.match(pattern);
    return matches ? matches.length : 0;
  }, []);

  // 参照回数カウント関数のref
  const countReferencesRef = useRef(countElementReferences);

  // コールバック関数のref（CodeMirror内から最新のコールバックを参照するため）
  const callbacksRef = useRef({
    onDeleteElement,
    onEditElement,
    onDuplicateElement,
    onOpenSourceDataset,
    onExportElement,
    onResizeElement
  });

  // 要素変更を通知するためのStateEffect
  const elementsChangedEffect = useRef(StateEffect.define<null>());

  // 要素プレビューの開閉状態（デフォルトは全て閉じた状態）
  const [expandedElements, setExpandedElements] = useState<Set<string>>(new Set());

  const toggleElement = useCallback((elementId: string) => {
    setExpandedElements(prev => {
      const next = new Set(prev);
      if (next.has(elementId)) {
        next.delete(elementId);
      } else {
        next.add(elementId);
      }
      return next;
    });
  }, []);

  // コールバック参照を保持（常に最新を参照するため）
  const onChangeRef = useRef(onChange);
  const onCloseRef = useRef(onClose);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    onChangeRef.current = onChange;
    onCloseRef.current = onClose;
  }, [onChange, onClose]);

  // elements/datasetsが変更されたらrefを更新し、CodeMirrorに再描画を通知
  useEffect(() => {
    elementsRef.current = elements;
    datasetsRef.current = datasets;

    // CodeMirrorにelementsの変更を通知してdecorationsを再計算させる
    if (editorViewRef.current) {
      editorViewRef.current.dispatch({
        effects: elementsChangedEffect.current.of(null)
      });
    }
  }, [elements, datasets]);

  // コールバック関数のrefを更新
  useEffect(() => {
    callbacksRef.current = {
      onDeleteElement,
      onEditElement,
      onDuplicateElement,
      onOpenSourceDataset,
      onExportElement,
      onResizeElement
    };
  }, [onDeleteElement, onEditElement, onDuplicateElement, onOpenSourceDataset, onExportElement, onResizeElement]);

  // 参照回数カウント関数のrefを更新
  useEffect(() => {
    countReferencesRef.current = countElementReferences;
  }, [countElementReferences]);

  // デバウンス付きonChange呼び出し
  const debouncedOnChange = useCallback((newContent: string) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      onChangeRef.current(newContent);
    }, 300);
  }, []);

  // コンポーネントアンマウント時にタイマーをクリア
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // 現在のエディタ内容を取得
  const getCurrentContent = useCallback(() => {
    return editorViewRef.current?.state.doc.toString() || content;
  }, [content]);

  // CodeMirrorエディタの初期化
  useLayoutEffect(() => {
    if (!editorContainerRef.current || editorViewRef.current) return;

    // 要素参照の補完候補を生成
    const getElementCompletions = () => {
      return Object.entries(elements).map(([id, element]) => {
        const title = 'title' in element && element.title ? element.title : null;
        const displayLabel = title
          ? `${title} (${element.type})`
          : `${element.type}: ${id.slice(0, 8)}...`;
        return {
          label: displayLabel,
          type: 'variable',
          apply: `{{${element.type}:${id}}}`
        };
      });
    };

    // オートコンプリート設定
    const elementAutocompletion = autocompletion({
      override: [
        (context: CompletionContext) => {
          // {{ の後の入力を検出
          const beforeCursor = context.matchBefore(/\{\{[\w_:-]*/);
          if (beforeCursor) {
            return {
              from: beforeCursor.from,
              options: getElementCompletions(),
              filter: false // labelではなく全候補を表示
            };
          }
          return null;
        }
      ]
    });

    // キーバインディング（Ctrl+S / Escape でエディタを閉じる）
    const editorKeymap = Prec.highest(keymap.of([
      {
        key: 'Mod-s',
        run: () => {
          // 即座に保存してからエディタを閉じる
          const currentContent = editorViewRef.current?.state.doc.toString() || '';
          onChangeRef.current(currentContent);
          onCloseRef.current();
          return true;
        },
        preventDefault: true
      },
      {
        key: 'Escape',
        run: () => {
          onCloseRef.current();
          return true;
        }
      }
    ]));

    // ドキュメント変更時にデバウンス付きでonChangeを呼び出す
    const changeListener = EditorView.updateListener.of((update) => {
      if (update.docChanged) {
        debouncedOnChange(update.state.doc.toString());
      }
    });

    // インラインプレビューウィジェット（ReactコンポーネントをCodeMirror内に描画）
    class ElementPreviewWidget extends WidgetType {
      private root: Root | null = null;

      constructor(
        readonly elementType: string,
        readonly elementId: string,
        readonly element: ReportElementUnion | null,
        readonly elementDatasets: Record<string, DataSet>,
        readonly width: number | 'fit',
        readonly height: number,
        readonly aspectRatio: AspectRatioPreset
      ) {
        super();
      }

      toDOM() {
        const container = document.createElement('div');
        container.className = 'cm-element-preview-widget';
        container.setAttribute('data-element-id', this.elementId);

        // コールバックをrefから取得（最新の値を使用）
        const callbacks = callbacksRef.current;

        // 参照回数をカウント
        const refCount = countReferencesRef.current(this.elementId);

        // Reactコンポーネントをマウント
        this.root = createRoot(container);
        this.root.render(
          <ElementPreviewInline
            elementType={this.elementType}
            elementId={this.elementId}
            element={this.element}
            datasets={this.elementDatasets}
            width={this.width}
            height={this.height}
            aspectRatio={this.aspectRatio}
            onDeleteElement={callbacks.onDeleteElement}
            onEditElement={callbacks.onEditElement}
            onDuplicateElement={callbacks.onDuplicateElement}
            onOpenSourceDataset={callbacks.onOpenSourceDataset}
            onExportElement={callbacks.onExportElement}
            onResizeElement={callbacks.onResizeElement}
            referenceCount={refCount}
          />
        );

        return container;
      }

      destroy() {
        // Reactルートをアンマウント
        if (this.root) {
          // 非同期でアンマウント（Reactの警告を避けるため）
          setTimeout(() => {
            this.root?.unmount();
            this.root = null;
          }, 0);
        }
      }

      ignoreEvent() {
        // クリックイベントなどをReactコンポーネントに伝播させる
        return false;
      }

      eq(other: ElementPreviewWidget) {
        return (
          other.elementType === this.elementType &&
          other.elementId === this.elementId &&
          other.element === this.element &&
          other.aspectRatio === this.aspectRatio
        );
      }
    }

    // {{type:id}} パターンを検出してインラインプレビューに置き換えるプラグイン
    const elementPattern = /\{\{(\w+):([^}]+)\}\}/g;

    // elementsChangedEffectへの参照をローカル変数に保存（クロージャ用）
    const effectRef = elementsChangedEffect.current;

    function createPreviewDecorations(view: EditorView): DecorationSet {
      const decorations: Range<Decoration>[] = [];
      const doc = view.state.doc;
      // refから最新のelements/datasetsを取得（クロージャの初期値ではなく最新値）
      const currentElements = elementsRef.current;
      const currentDatasets = datasetsRef.current;

      for (let i = 1; i <= doc.lines; i++) {
        const line = doc.line(i);
        let match;
        elementPattern.lastIndex = 0;

        while ((match = elementPattern.exec(line.text)) !== null) {
          const elementType = match[1];
          const elementId = match[2];
          const element = currentElements[elementId] || null;
          const width = element?.width ?? REPORT_ELEMENT_SIZING.DEFAULT_WIDTH;
          const height = element?.height ?? REPORT_ELEMENT_SIZING.DEFAULT_HEIGHT;
          // aspectRatio未設定は'custom'として扱い、heightを使用（後方互換性）
          const aspectRatio = element?.aspectRatio ?? 'custom';

          const from = line.from + match.index;
          const to = from + match[0].length;

          decorations.push(
            Decoration.replace({
              widget: new ElementPreviewWidget(
                elementType,
                elementId,
                element,
                currentDatasets,
                width,
                height,
                aspectRatio
              )
              // block: true は ViewPlugin では使用不可。CSSで display: block を設定
            }).range(from, to)
          );
        }
      }

      return Decoration.set(decorations);
    }

    const elementPreviewPlugin = ViewPlugin.fromClass(
      class {
        decorations: DecorationSet;

        constructor(view: EditorView) {
          this.decorations = createPreviewDecorations(view);
        }

        update(update: ViewUpdate) {
          // ドキュメント変更、ビューポート変更、または要素変更時に再描画
          const hasElementsChanged = update.transactions.some(
            tr => tr.effects.some(e => e.is(effectRef))
          );
          if (update.docChanged || update.viewportChanged || hasElementsChanged) {
            this.decorations = createPreviewDecorations(update.view);
          }
        }
      },
      {
        decorations: (v) => v.decorations
      }
    );

    // インラインプレビューウィジェットのスタイル
    const previewWidgetTheme = EditorView.baseTheme({
      '.cm-element-preview-widget': {
        display: 'block',
        margin: '8px 0',
      }
    });

    // エディタの状態を作成
    const startState = EditorState.create({
      doc: content,
      extensions: [
        lineNumbers(),
        markdown(),
        elementAutocompletion,
        editorKeymap,
        keymap.of([...defaultKeymap, ...searchKeymap, indentWithTab]),
        changeListener,
        elementPreviewPlugin,
        previewWidgetTheme,
        EditorView.lineWrapping,
        EditorView.theme({
          "&": {
            fontSize: "14px",
            border: "1px solid #ced4da",
            borderRadius: "6px",
            height: "100%",
            minHeight: "300px",
            backgroundColor: "#fff"
          },
          ".cm-scroller": {
            fontFamily: "'Monaco', 'Consolas', 'Courier New', monospace",
            lineHeight: "1.6",
          },
          ".cm-content": {
            minHeight: "280px",
            padding: "12px",
          },
          "&.cm-focused": {
            outline: "none",
            borderColor: "#007bff",
            boxShadow: "0 0 0 2px rgba(0, 123, 255, 0.25)"
          },
          ".cm-gutters": {
            backgroundColor: "#f8f9fa",
            borderRight: "1px solid #e9ecef"
          }
        }),
        editableCompartment.current.of(EditorView.editable.of(true)),
      ]
    });

    // エディタビューを作成
    const view = new EditorView({
      state: startState,
      parent: editorContainerRef.current
    });

    editorViewRef.current = view;

    // フォーカスを設定
    view.focus();

    // クリーンアップ
    return () => {
      if (editorViewRef.current) {
        editorViewRef.current.destroy();
        editorViewRef.current = null;
      }
    };
    // CodeMirrorエディタは一度だけ初期化し、以降は内部状態で管理する
    // contentとelementsの初期値のみ使用し、変更時の再初期化は不要
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 閉じるハンドラ（Ctrl+S押下時、即時保存してから閉じる）
  const handleClose = useCallback(() => {
    // 即座に保存してからエディタを閉じる
    onChange(getCurrentContent());
    onClose();
  }, [onChange, onClose, getCurrentContent]);

  return (
    <div className={styles.editorContainer}>
      <div className={styles.editorToolbar}>
        <div className={styles.toolbarHint}>
          <span className={styles.shortcutHint}>
            <kbd>Ctrl</kbd>+<kbd>S</kbd> / <kbd>Esc</kbd> Done
          </span>
          <span className={styles.shortcutHint}>
            Type <code>{'{{'}</code> for element suggestions
          </span>
          <span className={styles.shortcutHint}>
            (Auto-saved)
          </span>
        </div>
        <div className={styles.toolbarButtons}>
          <button
            className={styles.saveButton}
            onClick={handleClose}
          >
            Done
          </button>
        </div>
      </div>

      <div
        ref={editorContainerRef}
        className={styles.editorWrapper}
      />

      {/* 利用可能な要素のリスト（プレビュー付き） */}
      {Object.keys(elements).length > 0 && (
        <div className={styles.elementsList} data-testid="report-elements-list">
          <strong>Report Elements:</strong>
          <div className={styles.elementsGrid}>
            {Object.entries(elements).map(([id, element]) => {
              const title = 'title' in element && element.title ? element.title : null;
              const width = element.width ?? REPORT_ELEMENT_SIZING.DEFAULT_WIDTH;
              const height = element.height ?? REPORT_ELEMENT_SIZING.DEFAULT_HEIGHT;
              // aspectRatio未設定は'custom'として扱い、heightを使用（後方互換性）
              const aspectRatio = element.aspectRatio ?? 'custom';
              const aspectRatioCss = getAspectRatioCssValue(aspectRatio);
              const isExpanded = expandedElements.has(id);
              return (
                <div key={id} className={styles.elementCard}>
                  <div
                    className={styles.elementCardHeader}
                    onClick={() => toggleElement(id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <span className={styles.expandIcon}>{isExpanded ? '▼' : '▶'}</span>
                    <code
                      className={styles.elementCode}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(`{{${element.type}:${id}}}`);
                      }}
                      title="Click to copy reference"
                    >
                      {title || element.type}
                    </code>
                  </div>
                  {isExpanded && (
                    <div
                      className={styles.elementPreview}
                      style={{
                        width: width === 'fit' ? '100%' : `${width}px`,
                        aspectRatio: aspectRatioCss,
                        height: aspectRatio === 'custom' ? `${height}px` : undefined,
                      }}
                    >
                      <div className={styles.elementMenuWrapper}>
                        <ReportElementMenu
                          elementId={id}
                          elementType={element.type}
                          onDeleteElement={onDeleteElement || (() => {})}
                          onEditElement={onEditElement}
                          onDuplicateElement={onDuplicateElement}
                          onOpenSourceDataset={onOpenSourceDataset}
                          onExportElement={onExportElement}
                          onResizeElement={onResizeElement}
                          hasDatasetId={'datasetId' in element && !!element.datasetId}
                          referenceCount={countElementReferences(id)}
                          context="list"
                        />
                      </div>
                      <ReportElementRenderer
                        element={element}
                        datasets={datasets}
                        width={width}
                        // aspectRatio指定時はheightを渡さない（親コンテナに合わせる）
                        height={aspectRatio === 'custom' ? height : undefined}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
